import { EUnitPermissionAreas, ISODateString, Uuid } from "@m7-health/shared-utils";
import { createSlice } from "@reduxjs/toolkit";

import { IUser } from "@/api";

import { IUserWithDetails, TUserErrors } from "../components/UserTable/types";
import {
  EStaffStatus,
  INoteModalData,
  IRosterState,
  ISingleSendSMSModalData,
  ISuspendModalData,
  RosterIdsByEntityOption,
} from "../types";

import { bulkCreateUsersActions, initialBulkCreateUsersState } from "./bulkCreateUsers";

import type { PayloadAction } from "@reduxjs/toolkit";

const initialState: IRosterState = {
  addUserModal: {
    isOpen: false,
  },
  staffRosterFiltersModal: {
    isOpen: false,
  },
  massSendSmsModal: {
    isOpen: false,
  },
  singleSendSmsModal: {
    isOpen: false,
    preSelectedUserToSms: undefined,
  },
  editSettingsModal: {
    isOpen: false,
    selectedSMSRosterIds: [],
    selectedEmailRosterIds: [],
    selectedRosterIdsByEntityOption: {},
  },
  staffProfileSidebar: {
    isOpen: false,
    staffId: null,
  },
  editStaffModal: {
    id: "",
    isOpen: false,
  },
  noteModal: {
    data: {
      firstName: "",
      id: "",
      lastName: "",
      note: "",
    },
    isOpen: false,
    variant: "add",
  },
  statusChangeModal: {
    isOpen: false,
    data: {
      id: "",
      name: "",
      status: EStaffStatus.Inactive,
      postDatedExistingStatus: null,
      postDatedExistingDate: null,
    },
  },
  suspendAccountModal: {
    isOpen: false,
    data: {
      id: "",
      name: "",
    },
  },
  restoreAccountModal: {
    isOpen: false,
    data: {
      id: "",
      name: "",
    },
  },
  sendCommunicationsModal: {
    isOpen: false,
    userIds: [],
  },
  updateRuleSetsModal: {
    isOpen: false,
  },
  updateRuleSetModal: {
    isOpen: false,
  },
  updateRuleSetUsersModal: {
    isOpen: false,
  },
  updatePreferencesTemplatesModal: {
    isOpen: false,
  },
  updatePreferencesTemplateModal: {
    isOpen: false,
  },
  updatePreferencesTemplateUsersModal: {
    isOpen: false,
  },
  applyPreferencesTemplatesModal: {
    isOpen: false,
  },
  updateStatusDateModal: {
    isOpen: false,
  },
  updateSuspensionDateModal: {
    isOpen: false,
  },
  bulkUsersUploadModal: {
    isOpen: false,
  },
  editFloatEligibilityModal: {
    isOpen: false,
  },
  editAttributeEligibilityModal: {
    isOpen: false,
  },
  rosterTable: {
    pageSize: 100,
  },
  permissions: {
    permissionsReadyToSubmit: false,
    permissionsByUnit: {},
    permissionsByFacility: {},
  },
  bulkCreateUsers: initialBulkCreateUsersState,
  staffRosterfilters: {
    staffTypes: [],
    employmentTypes: [],
    attributeKeys: [],
    requirementIds: [],
    preferenceTemplateIds: [],
    orientationStatus: [],
    preceptorStatus: [],
  },
  bulkUpdateStaff: {
    isActive: false,
    updatedUsers: {},
    updatedUsersErrors: {},
  },
};

export const rosterModalSlice = createSlice({
  name: "roster",
  initialState,
  reducers: {
    ...bulkCreateUsersActions,
    openUpdateStatusDateModal: (
      state,
      action: PayloadAction<NonNullable<IRosterState["updateStatusDateModal"]["data"]>>,
    ) => {
      state.updateStatusDateModal = {
        isOpen: true,
        data: action.payload,
      };
    },
    closeUpdateStatusDateModal: (state) => {
      state.updateStatusDateModal = { isOpen: false };
    },
    openUpdateSuspensionDateModal: (
      state,
      action: PayloadAction<NonNullable<IRosterState["updateSuspensionDateModal"]["data"]>>,
    ) => {
      state.updateSuspensionDateModal = {
        isOpen: true,
        data: action.payload,
      };
    },
    closeUpdateSuspensionDate: (state) => {
      state.updateSuspensionDateModal = { isOpen: false };
    },
    setIsAddUserModalOpen: (state, action: PayloadAction<boolean>) => {
      state.addUserModal.isOpen = action.payload;
    },
    setMassSendSmsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.massSendSmsModal.isOpen = action.payload;
    },
    setSingleSendSmsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.singleSendSmsModal.isOpen = action.payload;
    },
    setSingleSendSmsModalUser: (state, action: PayloadAction<ISingleSendSMSModalData>) => {
      state.singleSendSmsModal.preSelectedUserToSms = action.payload;
    },
    setEditSettingsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.editSettingsModal.isOpen = action.payload;
    },
    setStaffProfileSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.staffProfileSidebar.isOpen = action.payload;
    },
    setStaffProfileSidebarStaffId: (state, action: PayloadAction<Uuid | null>) => {
      state.staffProfileSidebar.staffId = action.payload;
    },
    setStaffRosterFiltersModalOpen: (state, action: PayloadAction<boolean>) => {
      state.staffRosterFiltersModal.isOpen = action.payload;
    },
    setStaffRosterFilters: (state, action: PayloadAction<IRosterState["staffRosterfilters"]>) => {
      state.staffRosterfilters = action.payload;
    },
    openModal: (
      state,
      action: PayloadAction<
        | "updateRuleSetsModal"
        | "updateRuleSetModal"
        | "updateRuleSetUsersModal"
        | "updatePreferencesTemplateModal"
        | "updatePreferencesTemplatesModal"
        | "updatePreferencesTemplateUsersModal"
        | "applyPreferencesTemplatesModal"
        | "sendCommunicationsModal"
      >,
    ) => {
      state[action.payload].isOpen = true;
    },
    setUsersToSendCommunications: (state, action: PayloadAction<IUser["id"][]>) => {
      state.sendCommunicationsModal.userIds = action.payload;
    },
    setRuleSetToUpdate: (
      state,
      action: PayloadAction<IRosterState["updateRuleSetModal"]["ruleSetToUpdate"]>,
    ) => {
      state.updateRuleSetModal.ruleSetToUpdate = action.payload;
    },
    setRuleSetToUpdateUsers: (
      state,
      action: PayloadAction<IRosterState["updateRuleSetUsersModal"]["ruleSetToUpdate"]>,
    ) => {
      state.updateRuleSetUsersModal.ruleSetToUpdate = action.payload;
    },
    setPreferencesTemplateToUpdate: (
      state,
      action: PayloadAction<
        IRosterState["updatePreferencesTemplateModal"]["preferencesTemplateToUpdate"]
      >,
    ) => {
      state.updatePreferencesTemplateModal.preferencesTemplateToUpdate = action.payload;
    },
    setPreferencesTemplateToUpdateUsers: (
      state,
      action: PayloadAction<
        IRosterState["updatePreferencesTemplateUsersModal"]["preferencesTemplateToUpdate"]
      >,
    ) => {
      state.updatePreferencesTemplateUsersModal.preferencesTemplateToUpdate = action.payload;
    },
    closeModal: (
      state,
      action: PayloadAction<
        | "updateRuleSetsModal"
        | "updateRuleSetModal"
        | "updateRuleSetUsersModal"
        | "updatePreferencesTemplateModal"
        | "updatePreferencesTemplatesModal"
        | "updatePreferencesTemplateUsersModal"
        | "applyPreferencesTemplatesModal"
        | "sendCommunicationsModal"
      >,
    ) => {
      state[action.payload].isOpen = false;
    },
    setEditSettingsModalSelectedSMSRosterIds: (state, action: PayloadAction<number[]>) => {
      state.editSettingsModal.selectedSMSRosterIds = action.payload;
    },
    setEditSettingsModalSelectedEmailRosterIds: (state, action: PayloadAction<number[]>) => {
      state.editSettingsModal.selectedEmailRosterIds = action.payload;
    },
    setEditSettingsModalSelectedRosterIdsByEntityOption: (
      state,
      action: PayloadAction<RosterIdsByEntityOption>,
    ) => {
      state.editSettingsModal.selectedRosterIdsByEntityOption = action.payload;
    },
    setIsEditStaffModalOpen: (state, action: PayloadAction<boolean>) => {
      state.editStaffModal.isOpen = action.payload;
    },
    setEditStaffModalId: (state, action: PayloadAction<string>) => {
      state.editStaffModal.id = action.payload;
    },
    setIsNoteModalOpen: (state, action: PayloadAction<boolean>) => {
      state.noteModal.isOpen = action.payload;
    },
    setNoteModalData: (state, action: PayloadAction<INoteModalData>) => {
      state.noteModal.data = action.payload;
    },
    setNoteModalVariant: (state, action: PayloadAction<"add" | "edit">) => {
      state.noteModal.variant = action.payload;
    },
    setIsStatusChangeModalOpen: (state, action: PayloadAction<boolean>) => {
      state.statusChangeModal.isOpen = action.payload;
    },
    setIsBulkUsersUploadModalOpen: (state, action: PayloadAction<boolean>) => {
      state.bulkUsersUploadModal.isOpen = action.payload;
    },
    setStatusChangeModalData: (
      state,
      action: PayloadAction<{
        name: string;
        id: string;
        status: EStaffStatus;
        postDatedExistingStatus: "inactive" | "suspended" | null;
        postDatedExistingDate: ISODateString | null;
      }>,
    ) => {
      state.statusChangeModal.data = action.payload;
    },
    setIsSuspendedModalOpen: (state, action: PayloadAction<boolean>) => {
      state.suspendAccountModal.isOpen = action.payload;
    },
    setSuspendedModalData: (state, action: PayloadAction<ISuspendModalData>) => {
      state.suspendAccountModal.data = action.payload;
    },
    setRosterTablePageSize: (state, action: PayloadAction<number>) => {
      state.rosterTable.pageSize = action.payload;
    },
    setIsRestoredModalOpen: (state, action: PayloadAction<boolean>) => {
      state.restoreAccountModal.isOpen = action.payload;
    },
    setRestoredModalData: (state, action: PayloadAction<ISuspendModalData>) => {
      state.restoreAccountModal.data = action.payload;
    },
    setPermissionsByUnit: (
      state,
      action: PayloadAction<Record<string, Record<EUnitPermissionAreas, string>>>,
    ) => {
      state.permissions.permissionsByUnit = action.payload;
    },
    setPermissionForUnit: (
      state,
      action: PayloadAction<{
        unitId: string;
        permissionArea: EUnitPermissionAreas;
        permission: string;
      }>,
    ) => {
      state.permissions.permissionsByUnit = {
        ...state.permissions.permissionsByUnit,
        [action.payload.unitId]: {
          ...state.permissions.permissionsByUnit[action.payload.unitId],
          [action.payload.permissionArea]: action.payload.permission,
        },
      } as Record<string, Record<EUnitPermissionAreas, string>>;
    },
    setPermissionsByFacility: (
      state,
      action: PayloadAction<Record<string, Record<EUnitPermissionAreas, string>>>,
    ) => {
      state.permissions.permissionsByFacility = action.payload;
    },
    setPermissionForFacility: (
      state,
      action: PayloadAction<{
        facilityId: string;
        permissionArea: EUnitPermissionAreas;
        permission: string;
      }>,
    ) => {
      state.permissions.permissionsByFacility = {
        ...state.permissions.permissionsByFacility,
        [action.payload.facilityId]: {
          ...state.permissions.permissionsByFacility[action.payload.facilityId],
          [action.payload.permissionArea]: action.payload.permission,
        },
      } as Record<string, Record<EUnitPermissionAreas, string>>;
    },
    setPermissionsReadyToSubmit: (state, action: PayloadAction<boolean>) => {
      state.permissions.permissionsReadyToSubmit = action.payload;
    },
    setIsBulkUpdateStaffActive: (state, action: PayloadAction<boolean>) => {
      state.bulkUpdateStaff.isActive = action.payload;
    },
    setUpdatedUsers: (state, action: PayloadAction<Record<string, IUserWithDetails>>) => {
      state.bulkUpdateStaff.updatedUsers = {
        ...state.bulkUpdateStaff.updatedUsers,
        ...action.payload,
      };
    },
    clearUpdatedUsers: (state) => {
      state.bulkUpdateStaff.updatedUsers = {};
    },
    setUpdatedUsersErrors: (state, action: PayloadAction<TUserErrors>) => {
      state.bulkUpdateStaff.updatedUsersErrors = action.payload;
    },
  },
});

export const {
  openUpdateStatusDateModal,
  closeUpdateStatusDateModal,
  openUpdateSuspensionDateModal,
  closeUpdateSuspensionDate,
  setEditStaffModalId,
  setIsAddUserModalOpen,
  setMassSendSmsModalOpen,
  setUsersToSendCommunications,
  setSingleSendSmsModalOpen,
  setSingleSendSmsModalUser,
  setEditSettingsModalOpen,
  setEditSettingsModalSelectedSMSRosterIds,
  setEditSettingsModalSelectedEmailRosterIds,
  setEditSettingsModalSelectedRosterIdsByEntityOption,
  setIsEditStaffModalOpen,
  setRosterTablePageSize,
  setIsNoteModalOpen,
  setIsSuspendedModalOpen,
  setPreferencesTemplateToUpdate,
  setPreferencesTemplateToUpdateUsers,
  openModal,
  closeModal,
  setRuleSetToUpdate,
  setRuleSetToUpdateUsers,
  setNoteModalData,
  setNoteModalVariant,
  setIsStatusChangeModalOpen,
  setIsBulkUsersUploadModalOpen,
  setStatusChangeModalData,
  setSuspendedModalData,
  setIsRestoredModalOpen,
  setRestoredModalData,
  setPermissionsByUnit,
  setPermissionsReadyToSubmit,
  setStaffRosterFiltersModalOpen,
  setStaffRosterFilters,
  setIsBulkUpdateStaffActive,
  setStaffProfileSidebarOpen,
  setStaffProfileSidebarStaffId,
  setUpdatedUsers,
  clearUpdatedUsers,
  setUpdatedUsersErrors,
  setPermissionsByFacility,
  setPermissionForFacility,
  setPermissionForUnit,
} = rosterModalSlice.actions;
export const RosterActions = rosterModalSlice.actions;
export default rosterModalSlice.reducer;
